import { useCallback, useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { PlayerProfileHero, type Breadcrumb } from './hero';
import { PlayerProfileFilterBar } from './filter-bar';
import { FormChart } from './form-chart';
import type { FormChartPoint } from './form-chart';
import { PerformanceDeltas } from './performance-deltas';
import { UnderlyingMetrics } from './underlying-metrics';
import { DefensiveContribution } from './defensive-contribution';
import { UpcomingFixtures } from './upcoming-fixtures';
import type { UpcomingFixture } from './upcoming-fixtures';
import { GameweekLog, FREE_TEASER_ROW_COUNT } from './gameweek-log';
import type { GameweekLogRow } from './gameweek-log';
import { SummaryBlock } from './summary-block';
import { LockedModulePanel } from './locked-overlay';
import { EmptyModule } from './panel';
import {
  ChartSkeleton,
  FixturesSkeleton,
  MetricRowsSkeleton,
  ProfileSkeleton,
  TableSkeleton
} from './skeletons';
import { useAuth0User } from '@hooks/useAuth0User';
import { useIsAppView } from '@hooks/useIsAppView';
import { useAccessToken } from '@hooks/useAccessToken';
import { usePlayers } from '@hooks/usePlayers';
import { usePlayerStats } from '@hooks/usePlayerStats';
import { usePlayerHistory } from '@hooks/usePlayerHistory';
import { usePositionPercentiles } from '@hooks/usePositionPercentiles';
import { usePlayerProfileFilters } from '@hooks/usePlayerProfileFilters';
import { useRecentlyViewedPlayers } from '@hooks/useRecentlyViewedPlayers';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useTeams } from '@hooks/useTeams';
import { useTeamFixturesPreview } from '@hooks/useTeamFixturesPreview';

import Title from '@components/title';
import WideLayout from '@components/layout/wide';
import {
  MEMBERSHIPS,
  PAID_MEMBERSHIP_LEVELS,
  WEBSITE_NAME
} from '@lib/constants';
import { hasSufficientPrivileges, isPaidMember } from '@lib/utils';

import {
  DETAILED_STATS_FROM_SEASON,
  DEFENSIVE_CONTRIBUTION_FROM_SEASON,
  getDefensiveContributionRule,
  getUnderlyingMetrics,
  readStat,
  type DefensiveContributionBasis,
  type PlayerPosition
} from '@lib/page-utils/player-profile/metrics';
import {
  LAST_GAMEWEEK,
  FIRST_GAMEWEEK,
  decimalsForBasis,
  describeFilters,
  getSelectableSeasons,
  toAggregation,
  toGameweekRange,
  toSeasonLabel
} from '@lib/page-utils/player-profile/filters';
import { toPlayerByIdPath } from '@lib/page-utils/player-profile/slugs';
import type { Player as FfhPlayer } from '@api-service/players';
import type { PlayerStatsPosition } from '@api-service/player-stats';

const FFHModal = dynamic(() => import('@components/modal'));
const Paywall = dynamic(() => import('@components/paywall'));

const PROFILE_STAT_FIELDS =
  'fpl,shooting,creativity,defending,defensiveActions';
const HERO_STAT_FIELDS = 'fpl';

const UPCOMING_FIXTURE_COUNT = 6;
const MOST_OWNED_COUNT = 5;
const CLUBMATE_LINK_COUNT = 4;
const COMPARISON_LINK_COUNT = 3;
/**
 * The filter bar sits under the fixed header. The submenu below the header is
 * in normal flow and scrolls away with the page, so matching its height here
 * would leave a dead gap above the bar once the page is scrolled. In the app
 * view the header does not render at all, so the offset there is zero.
 */
const FIXED_HEADER_HEIGHT_PX = 74;

const PAYWALL_TITLE = 'Unlock the player profile tool 🔒';
const PLAYER_PROFILES_LANDING_PATH = '/player-profile';
const COMPARISON_TOOL_PATH = '/fpl-player-comparison-tool';
const OPTA_PATH = '/opta';

export type PlayerProfileViewProps = {
  optaId: string;
  /**
   * Prediction-free player row fetched at build time on the slug routes. It
   * feeds the server-rendered hero and summary so crawlers receive real
   * content, and never drives a query: the ids it carries are current-season
   * ids, which would poison season-scoped stats lookups.
   */
  staticPlayer?: FfhPlayer | null;
  /** Slug-route breadcrumb trail; the legacy route keeps the hero default. */
  breadcrumbs?: Breadcrumb[];
  /**
   * True when the hosting page renders its own full <Head>. The view then
   * renders no head tags at all, so page metadata and view fall back cannot
   * fight over the title.
   */
  hasOwnHead?: boolean;
};

export const PlayerProfileView = ({
  optaId,
  staticPlayer = null,
  breadcrumbs,
  hasOwnHead = false
}: PlayerProfileViewProps) => {
  const router = useRouter();
  const { isAppView } = useIsAppView();

  const [isPaywallOpen, setIsPaywallOpen] = useState(false);

  const { user } = useAuth0User();
  const { accessToken, accessTokenIsFetching } = useAccessToken();
  // A logged-in user's token resolves after first render. Queries that want
  // predictions must wait for it: firing without it asks for predictions
  // anonymously, which the API rejects as a 400 rather than zeroing them.
  // Anonymous visitors never resolve a token, so they must not wait.
  const isAwaitingAccessToken = Boolean(user) && accessTokenIsFetching;
  const hasPaidMembership = isPaidMember(user);
  const hasSufficientMembershipLevel = hasSufficientPrivileges(
    user,
    PAID_MEMBERSHIP_LEVELS[MEMBERSHIPS.STANDARD_MONTHLY_ID].LEVEL
  );
  const isRestricted = !(hasPaidMembership && hasSufficientMembershipLevel);

  const openPaywall = useCallback(() => setIsPaywallOpen(true), []);

  const { context, isLoading: contextIsLoading } = useLeagueContext();
  const currentSeason = context?.season ?? null;
  // PRE_SEASON means the next gameweek is GW1: the incoming season exists but
  // has no results yet, so the profile defaults to last season's numbers
  // rather than opening on empty modules. Any current gameweek the context
  // still reports at that point belongs to the season that just finished, so
  // it must not be read as this season's progress.
  const isPreSeason = context?.seasonStatus === 'PRE_SEASON';
  const latestGameweek = isPreSeason
    ? null
    : context?.currentGameweek?.number ?? null;
  const nextGameweekNumber = context?.nextGameweek?.number ?? null;
  // Every season-gated decision below reads `filters.season`, which falls back
  // to 0 until the context resolves. Rendering through that window would show a
  // season of 0 as "before Opta counts existed" and flash the wrong empty
  // states, so the profile waits for a real season.
  const hasResolvedSeason = currentSeason !== null;
  const defaultSeason =
    isPreSeason && currentSeason !== null ? currentSeason - 1 : currentSeason;

  const { filters, setFilter } = usePlayerProfileFilters(defaultSeason);
  const isCurrentSeason = filters.season === currentSeason;

  // A past season is complete, so its window runs to the final gameweek; the
  // current season runs only as far as results exist.
  const windowEndGameweek = isCurrentSeason
    ? latestGameweek ?? FIRST_GAMEWEEK
    : LAST_GAMEWEEK;

  const { minGameweek, maxGameweek } = toGameweekRange(
    filters.window,
    windowEndGameweek
  );

  const aggregation = toAggregation(filters.basis);
  const decimals = decimalsForBasis(filters.basis);

  // Local to the module rather than part of the shared filter state: it only
  // changes what one panel counts, and it is not worth a URL round trip.
  const [hitRateBasis, setHitRateBasis] =
    useState<DefensiveContributionBasis>('starts');

  const { teams } = useTeams();
  const { recordView } = useRecentlyViewedPlayers();

  /* ---------------------------------------------------------------------- */
  /* Player identity                                                        */
  /* ---------------------------------------------------------------------- */

  const { data: playersData, isLoading: playersIsLoading } = usePlayers({
    optaIds: optaId,
    limit: 1,
    enabled: !isAwaitingAccessToken,
    ...(filters.season ? { season: filters.season } : {}),
    ...(accessToken ? { accessToken } : {}),
    // Predictions need authentication. Asking for them anonymously is a 400,
    // not a zeroed payload, so every tokenless request must opt out - free
    // visitors always, and members whose token failed to refresh.
    ...(isRestricted || !accessToken ? { exclude: 'predictions' } : {})
  });

  const player = playersData?.pages[0]?.data[0] ?? null;

  // Player rows are minted per season, and a row's fixtures belong to that
  // row's season. Whenever the stats season is not the current one (a past
  // season chip, or the pre-season default of last season), the current
  // season's row is fetched separately so upcoming fixtures, and pre-season
  // the price and availability too, come from the season they belong to.
  const {
    data: currentSeasonPlayersData,
    isLoading: currentSeasonPlayersIsLoading
  } = usePlayers({
    optaIds: optaId,
    limit: 1,
    enabled: hasResolvedSeason && !isCurrentSeason && !isAwaitingAccessToken,
    ...(accessToken ? { accessToken } : {}),
    ...(isRestricted || !accessToken ? { exclude: 'predictions' } : {})
  });

  const currentSeasonPlayer = isCurrentSeason
    ? player
    : currentSeasonPlayersData?.pages[0]?.data[0] ?? null;

  // Pre-season the page leads on who the player is now (club, price, flag)
  // while the numbers stay last season's; in season the stats-season row
  // already is the identity.
  const identityPlayer = isPreSeason ? currentSeasonPlayer ?? player : player;

  const playerId = player?.id ?? null;
  const position = (identityPlayer?.position ?? null) as PlayerPosition | null;
  const teamShortName = identityPlayer?.team.shortName ?? null;

  const teamId = useMemo(
    () => teams.find((team) => team.shortName === teamShortName)?.id ?? null,
    [teams, teamShortName]
  );

  useEffect(() => {
    if (!identityPlayer) return;
    recordView({
      optaId,
      displayName: identityPlayer.displayName,
      position: identityPlayer.position,
      teamShortName: identityPlayer.team.shortName,
      seasonPoints: player?.seasonStats?.points?.total ?? null
    });
  }, [identityPlayer, player, optaId, recordView]);

  /* ---------------------------------------------------------------------- */
  /* Season-level hero numbers, independent of the filter bar               */
  /* ---------------------------------------------------------------------- */

  // Totals rather than per-appearance, because the hero shows the selected
  // season's points total; the per-game figures divide out locally. The
  // players list cannot supply the total: its seasonStats are always the
  // current season's, which reads as zero against any past season.
  const { data: heroStatsData } = usePlayerStats({
    ids: playerId ?? undefined,
    fields: HERO_STAT_FIELDS,
    aggregation: 'total',
    venue: 'all',
    minGameweek: FIRST_GAMEWEEK,
    maxGameweek: LAST_GAMEWEEK,
    limit: 1,
    enabled: playerId != null,
    ...(filters.season ? { season: filters.season } : {}),
    ...(accessToken ? { accessToken } : {})
  });

  const heroStatsRow = heroStatsData?.pages[0]?.data[0];
  const heroAppearances = readStat(heroStatsRow, 'participation.appearances');
  const toHeroPerGame = (total: number | null): number | null =>
    total !== null && heroAppearances !== null && heroAppearances > 0
      ? total / heroAppearances
      : null;

  const heroTotalPoints = readStat(heroStatsRow, 'fpl.points');
  const heroSeasonPoints =
    heroTotalPoints === null ? null : Math.round(heroTotalPoints);
  const pointsPerGame = toHeroPerGame(heroTotalPoints);
  const expectedPointsPerGame = toHeroPerGame(
    readStat(heroStatsRow, 'fpl.expectedPoints')
  );
  const heroMinutes = readStat(heroStatsRow, 'participation.minutes');
  const heroStarts = readStat(heroStatsRow, 'participation.starts');
  const minutesPerStart =
    heroMinutes !== null && heroStarts !== null && heroStarts > 0
      ? heroMinutes / heroStarts
      : null;

  /* ---------------------------------------------------------------------- */
  /* Filtered aggregate stats                                               */
  /* ---------------------------------------------------------------------- */

  // Free visitors see every module this row feeds either locked or hidden,
  // so their request carries only the fpl group: the paywalled underlying
  // numbers never reach the client. The appearances count the free view does
  // read arrives with every response regardless of the fields asked for.
  const { data: statsData, isLoading: statsIsLoading } = usePlayerStats({
    ids: playerId ?? undefined,
    fields: isRestricted ? HERO_STAT_FIELDS : PROFILE_STAT_FIELDS,
    aggregation,
    venue: filters.venue,
    minGameweek,
    maxGameweek,
    limit: 1,
    enabled: playerId != null,
    ...(filters.season ? { season: filters.season } : {}),
    ...(accessToken ? { accessToken } : {})
  });

  const statsRow = statsData?.pages[0]?.data[0];

  /* ---------------------------------------------------------------------- */
  /* Per-gameweek stats                                                     */
  /* ---------------------------------------------------------------------- */

  // One request covers every gameweek, and every row carries its own expected
  // goals, expected assists, defensive tally and expected points. Because
  // history returns a row per fixture rather than per gameweek, each leg of a
  // double gameweek keeps its own numbers instead of being merged.
  // History has no field selection, so a free visitor's request is bounded by
  // row count instead: they receive exactly the rows the log teaser renders and
  // nothing beyond it. That also bounds the free form chart to those gameweeks,
  // which is the accepted cost of not shipping withheld rows to the client.
  const { data: historyData } = usePlayerHistory({
    id: playerId ?? undefined,
    limit: isRestricted ? FREE_TEASER_ROW_COUNT : LAST_GAMEWEEK,
    season: filters.season || undefined,
    enabled: playerId != null
  });

  /* ---------------------------------------------------------------------- */
  /* Percentiles                                                            */
  /* ---------------------------------------------------------------------- */

  const underlying = useMemo(
    () => (position ? getUnderlyingMetrics(position) : null),
    [position]
  );

  const hasDetailedStats = filters.season >= DETAILED_STATS_FROM_SEASON;

  const visibleMetrics = useMemo(
    () =>
      (underlying?.metrics ?? []).filter(
        (metric) => hasDetailedStats || !metric.needsDetailedStats
      ),
    [underlying, hasDetailedStats]
  );

  const percentileStatPaths = useMemo(
    () => visibleMetrics.map((metric) => metric.statPath),
    [visibleMetrics]
  );

  const { getPercentile, populationSize } = usePositionPercentiles({
    position: position as PlayerStatsPosition | null,
    fields: PROFILE_STAT_FIELDS,
    statPaths: percentileStatPaths,
    excludeRowId: playerId,
    aggregation,
    venue: filters.venue,
    minGameweek,
    maxGameweek,
    season: filters.season || null,
    enabled:
      !isRestricted && position != null && percentileStatPaths.length > 0,
    ...(accessToken ? { accessToken } : {})
  });

  /* ---------------------------------------------------------------------- */
  /* Fixtures                                                               */
  /* ---------------------------------------------------------------------- */

  // The context's next gameweek is authoritative for where the upcoming
  // window starts; pre-season that is GW1, before any gameweek has finished.
  const fixtureWindowStart =
    nextGameweekNumber ?? (latestGameweek ?? FIRST_GAMEWEEK) + 1;
  const { teams: fixturePreviewTeams } = useTeamFixturesPreview({
    gameweekStart: Math.min(fixtureWindowStart, LAST_GAMEWEEK),
    gameweekEnd: Math.min(
      fixtureWindowStart + UPCOMING_FIXTURE_COUNT - 1,
      LAST_GAMEWEEK
    ),
    ...(currentSeason ? { season: currentSeason } : {})
  });

  // Matched on the current season's team, because that is whose fixtures the
  // upcoming module shows even when a past season's stats are on screen.
  const upcomingTeamShortName = currentSeasonPlayer?.team.shortName ?? null;

  const difficultyByGameweek = useMemo(() => {
    const lookup = new Map<number, number>();
    const teamPreview = fixturePreviewTeams.find(
      (previewTeam) => previewTeam.shortName === upcomingTeamShortName
    );

    for (const fixture of teamPreview?.fixtures ?? []) {
      const difficulty = fixture.inferences?.difficulty.overall;
      if (typeof difficulty === 'number') {
        lookup.set(fixture.gameweek, Math.round(difficulty));
      }
    }

    return lookup;
  }, [fixturePreviewTeams, upcomingTeamShortName]);

  const upcomingFixtures = useMemo<UpcomingFixture[]>(() => {
    const byGameweek = new Map<number, UpcomingFixture>();

    // Only the current season's row carries genuinely upcoming fixtures; a
    // past-season row's fixtures are last season's rounds and must never
    // render as upcoming.
    for (const fixture of currentSeasonPlayer?.fixtures ?? []) {
      const existing = byGameweek.get(fixture.gameweek);
      const match = {
        opponentShortName: fixture.opponent.shortName,
        isHome: fixture.isHome,
        difficulty: difficultyByGameweek.get(fixture.gameweek) ?? null
      };

      if (existing) {
        // A double gameweek: both opponents stack in one cell and the
        // predictions sum, matching how the model splits them per fixture.
        existing.matches.push(match);
        existing.predictedPoints =
          (existing.predictedPoints ?? 0) + fixture.predictions.points;
        existing.expectedMinutes =
          (existing.expectedMinutes ?? 0) + fixture.predictions.minutes;
        continue;
      }

      byGameweek.set(fixture.gameweek, {
        gameweek: fixture.gameweek,
        matches: [match],
        predictedPoints: fixture.predictions.points,
        expectedMinutes: fixture.predictions.minutes
      });
    }

    return [...byGameweek.values()]
      .sort((first, second) => first.gameweek - second.gameweek)
      .slice(0, UPCOMING_FIXTURE_COUNT);
  }, [currentSeasonPlayer, difficultyByGameweek]);

  /* ---------------------------------------------------------------------- */
  /* Derived module data                                                    */
  /* ---------------------------------------------------------------------- */

  // History runs newest first and covers the whole season. The charts want the
  // selected window, oldest first, so they read left to right chronologically.
  const windowedHistoryRows = useMemo(
    () =>
      (historyData?.data ?? [])
        .filter(
          (row) => row.gameweek >= minGameweek && row.gameweek <= maxGameweek
        )
        .slice()
        .reverse(),
    [historyData, minGameweek, maxGameweek]
  );

  const defensiveRule = position
    ? getDefensiveContributionRule(position)
    : null;
  const hasDefensiveContribution =
    defensiveRule !== null &&
    filters.season >= DEFENSIVE_CONTRIBUTION_FROM_SEASON &&
    hasDetailedStats;

  /**
   * Both per-gameweek charts plot one column per gameweek, so the two legs of a
   * double gameweek are summed into a single column. The gameweek log keeps
   * them apart, because there the per-fixture split is the point.
   */
  const chartGameweeks = useMemo(() => {
    const byGameweek = new Map<
      number,
      {
        gameweek: number;
        opponentShortName: string;
        isHome: boolean;
        points: number;
        expectedPoints: number;
        defensiveActions: number;
        didStart: boolean;
        hasAppeared: boolean;
      }
    >();

    for (const row of windowedHistoryRows) {
      const actions = defensiveRule
        ? row.stats.defensiveActions[defensiveRule.historyKey]
        : 0;
      const existing = byGameweek.get(row.gameweek);

      if (existing) {
        existing.points += row.points;
        existing.expectedPoints += row.stats.expectedPoints;
        existing.defensiveActions += actions;
        // Across a double gameweek, starting or featuring in either leg counts.
        existing.didStart ||= row.stats.didStart;
        existing.hasAppeared ||= row.stats.minutes > 0;
        continue;
      }

      byGameweek.set(row.gameweek, {
        gameweek: row.gameweek,
        opponentShortName: row.opponent.shortName,
        isHome: row.isHome,
        points: row.points,
        expectedPoints: row.stats.expectedPoints,
        defensiveActions: actions,
        didStart: row.stats.didStart,
        hasAppeared: row.stats.minutes > 0
      });
    }

    return [...byGameweek.values()];
  }, [windowedHistoryRows, defensiveRule]);

  const formChartPoints = useMemo<FormChartPoint[]>(
    () =>
      chartGameweeks.map((gameweek) => ({
        gameweek: gameweek.gameweek,
        opponentShortName: gameweek.opponentShortName,
        isHome: gameweek.isHome,
        points: gameweek.points,
        expectedPoints: gameweek.expectedPoints
      })),
    [chartGameweeks]
  );

  const defensiveGameweeks = useMemo(
    () =>
      defensiveRule
        ? chartGameweeks.map((gameweek) => ({
            gameweek: gameweek.gameweek,
            actions: gameweek.defensiveActions,
            didStart: gameweek.didStart,
            hasAppeared: gameweek.hasAppeared
          }))
        : [],
    [chartGameweeks, defensiveRule]
  );

  const selectableSeasons = getSelectableSeasons(
    currentSeason ?? filters.season
  );
  // Only offered when the filter bar itself would offer it, so the log's
  // shortcut can never land on a season the chips cannot get back to.
  const previousSeason = selectableSeasons.includes(filters.season - 1)
    ? filters.season - 1
    : null;

  /**
   * Summed over the window rather than read off the stats row, which carries
   * the figure on the selected basis: per 90 that is a fraction, and rounding
   * it for display rendered almost every player as a flat 1.
   *
   * Null until the history query resolves. The page's loading gate covers
   * context, players and stats but not history, so summing an empty row set
   * would put a real-looking zero on screen while the rows are still in
   * flight.
   */
  const defensiveContributionPointsBanked = useMemo(
    () =>
      historyData
        ? windowedHistoryRows.reduce(
            (total, row) => total + row.stats.defensiveContributionPoints,
            0
          )
        : null,
    [historyData, windowedHistoryRows]
  );

  const gameweekLogRows = useMemo<GameweekLogRow[]>(
    () =>
      (historyData?.data ?? []).map((row) => ({
        gameweek: row.gameweek,
        opponentShortName: row.opponent.shortName,
        isHome: row.isHome,
        minutes: row.stats.minutes,
        goals: row.stats.goals,
        assists: row.stats.assists,
        expectedGoals: row.stats.expectedGoals,
        expectedAssists: row.stats.expectedAssists,
        defensiveActions: defensiveRule
          ? row.stats.defensiveActions[defensiveRule.historyKey]
          : null,
        points: row.points
      })),
    [historyData, defensiveRule]
  );

  /* ---------------------------------------------------------------------- */
  /* Clubmate and comparison links                                          */
  /* ---------------------------------------------------------------------- */

  // Feeds the profile's "compare with" links; the same query backs the
  // landing grid, so react-query dedupes it when both are mounted.
  const { data: mostOwnedData } = usePlayers({
    sortBy: 'ownership',
    sortDirection: 'desc',
    limit: MOST_OWNED_COUNT,
    exclude: 'predictions'
  });

  const mostOwned = useMemo(
    () =>
      (mostOwnedData?.pages.flatMap((page) => page.data) ?? []).map(
        (ownedPlayer) => ({
          optaId: ownedPlayer.externalIds?.optaUuid ?? null,
          displayName: ownedPlayer.displayName
        })
      ),
    [mostOwnedData]
  );

  const { data: clubmatesData } = usePlayers({
    teamId: teamId ?? undefined,
    sortBy: 'ownership',
    sortDirection: 'desc',
    limit: CLUBMATE_LINK_COUNT + 1,
    exclude: 'predictions',
    enabled: teamId != null && playerId != null
  });

  // These pills render client-side only, so the by-id resolver hop is
  // invisible to crawlers; building collision-safe slugs here would need the
  // whole squad for no reader-visible gain.
  const clubmateLinks = useMemo(
    () =>
      (clubmatesData?.pages.flatMap((page) => page.data) ?? [])
        .filter((clubmate) => clubmate.id !== playerId)
        .slice(0, CLUBMATE_LINK_COUNT)
        .flatMap((clubmate) => {
          const clubmateOptaId = clubmate.externalIds?.optaUuid;
          if (!clubmateOptaId) return [];
          return [
            {
              label: clubmate.displayName,
              href: toPlayerByIdPath(clubmateOptaId)
            }
          ];
        }),
    [clubmatesData, playerId]
  );

  const comparisonLinks = useMemo(() => {
    // Both sides travel in the URL so the comparison tool opens with the
    // pairing already selected rather than two empty pickers.
    return mostOwned
      .filter((owned) => owned.optaId !== null && owned.optaId !== optaId)
      .slice(0, COMPARISON_LINK_COUNT)
      .map((owned) => ({
        label: `${identityPlayer?.displayName ?? 'This player'} vs ${
          owned.displayName
        }`,
        href: `${COMPARISON_TOOL_PATH}?a=${optaId}&b=${owned.optaId}`
      }));
  }, [mostOwned, identityPlayer, optaId]);

  /* ---------------------------------------------------------------------- */
  /* Handlers                                                               */
  /* ---------------------------------------------------------------------- */

  const goToPlayer = useCallback(
    (nextOptaId: string) => {
      // The resolver route 301s to the canonical slug URL, so ad-hoc
      // navigation lands on the right page without knowing the slug here.
      router.push(toPlayerByIdPath(nextOptaId));
    },
    [router]
  );

  const handlePlayerClick = useCallback(
    (selectedPlayer: FfhPlayer) => {
      const nextOptaId =
        selectedPlayer.externalIds?.optaUuid ?? selectedPlayer.optaId;
      if (nextOptaId) goToPlayer(nextOptaId);
    },
    [goToPlayer]
  );

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */

  const paywallModal = (
    <FFHModal open={isPaywallOpen} handleClose={() => setIsPaywallOpen(false)}>
      <Paywall variant="modal" title={PAYWALL_TITLE} />
    </FFHModal>
  );

  const viewHead = (title: string) =>
    hasOwnHead ? null : (
      <Head>
        <title>{title}</title>
      </Head>
    );

  const isCoreLoading =
    contextIsLoading ||
    playersIsLoading ||
    (isPreSeason && !isCurrentSeason && currentSeasonPlayersIsLoading) ||
    // Gate on stats only while the query can actually run. It is keyed on the
    // player row's id, so with no row (a signing new to the league has none
    // for a past season) the query stays disabled and, disabled, isLoading
    // holds true forever - gating on it would deadlock the skeleton.
    (playerId != null && statsIsLoading);

  if (isCoreLoading) {
    // With no static row (the legacy query-param route) there is nothing
    // truthful to show yet, so the whole page is a skeleton.
    if (!staticPlayer) {
      return (
        <>
          {viewHead('Player profile')}
          <Title>Player profile</Title>
          <WideLayout content={false}>
            <ProfileSkeleton />
          </WideLayout>
        </>
      );
    }

    // The slug routes statically know who the player is, so the hero and the
    // summary prose render real content immediately - this is also exactly
    // what a crawler receives, since queries never run during static
    // generation. Only the stats modules wait for the client fetches.
    const staticTeamName =
      staticPlayer.team.fullName ?? staticPlayer.team.shortName;

    return (
      <div className="min-h-screen bg-gray-50">
        {viewHead(`${staticPlayer.displayName} | ${WEBSITE_NAME}`)}
        <Title>{staticPlayer.displayName}</Title>
        {paywallModal}

        <PlayerProfileHero
          displayName={staticPlayer.displayName}
          fullName={staticPlayer.fullName}
          position={staticPlayer.position}
          teamName={staticTeamName}
          price={staticPlayer.price}
          status={staticPlayer.status ?? null}
          chanceOfPlaying={staticPlayer.chanceOfPlaying ?? null}
          news={staticPlayer.news}
          newsAddedAt={staticPlayer.newsAddedAt}
          imageUrl={staticPlayer.imageUrl}
          kitImageUrl={staticPlayer.kitImageUrl}
          // Deliberately `|| null`, not `??`: the static row is always the
          // current season while the default filter season pre-season is the
          // one just finished, so echoing this row's 0 would contradict the
          // total the stats query is about to show. A real mid-season 0 shows
          // an em-dash for one fetch round trip, which is the cheaper wrong.
          seasonPoints={staticPlayer.seasonStats?.points?.total || null}
          pointsPerGame={null}
          ownership={staticPlayer.ownership ?? null}
          minutesPerStart={null}
          comparisonHref={`${COMPARISON_TOOL_PATH}?a=${optaId}`}
          optaHref={OPTA_PATH}
          breadcrumbs={breadcrumbs}
          onPlayerSearchClick={handlePlayerClick}
          onPaywallOpen={openPaywall}
        />

        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-5 sm:px-6 lg:px-12">
          <ChartSkeleton />
          <MetricRowsSkeleton />
          <FixturesSkeleton />
          <TableSkeleton />
          <SummaryBlock
            displayName={staticPlayer.displayName}
            position={staticPlayer.position}
            teamName={staticTeamName}
            season={currentSeason ?? 0}
            price={staticPlayer.price}
            ownership={staticPlayer.ownership ?? null}
            pointsPerGame={null}
            expectedPointsPerGame={null}
            comparisonLinks={[]}
            clubmateLinks={[]}
          />
        </div>
      </div>
    );
  }

  // Pre-season, or whenever the season cannot be resolved at all, every
  // season-gated module below would read a season of 0 and render as though the
  // data predated Opta counts. Say so instead.
  if (!hasResolvedSeason) {
    return (
      <>
        {viewHead('Player profile')}
        <WideLayout>
          <div className="py-16 text-center">
            <p className="font-sans text-xl font-bold text-neutral-800">
              Player profiles are unavailable right now
            </p>
            <p className="mt-2 text-sm text-neutral-500">
              We could not work out which season to show. This usually clears on
              its own between seasons — try again shortly.
            </p>
          </div>
        </WideLayout>
      </>
    );
  }

  // Pre-season a brand-new signing has no last-season row, so identity can
  // come from the incoming season's row while every stats module reports no
  // minutes for the selected season.
  if (!identityPlayer || !position || !underlying) {
    return (
      <>
        {viewHead('Player profile')}
        <WideLayout>
          <div className="py-16 text-center">
            <p className="font-sans text-xl font-bold text-neutral-800">
              We could not find that player
            </p>
            <p className="mt-2 text-sm text-neutral-500">
              They may not be in the {toSeasonLabel(filters.season)} squad list.
              Try searching again from the{' '}
              <a
                href={PLAYER_PROFILES_LANDING_PATH}
                className="text-primary-600 underline"
              >
                player profiles page
              </a>
              .
            </p>
          </div>
        </WideLayout>
      </>
    );
  }

  const hasPlayedInWindow =
    readStat(statsRow, 'participation.appearances') !== null &&
    (readStat(statsRow, 'participation.appearances') as number) > 0;

  const filterSubtitle = describeFilters(
    filters,
    maxGameweek - minGameweek + 1
  );

  const underlyingRows = visibleMetrics.map((metric) => {
    const value = readStat(statsRow, metric.statPath);
    return {
      metric,
      value,
      percentile: getPercentile(metric.statPath, value)
    };
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {viewHead(`${identityPlayer.displayName} | ${WEBSITE_NAME}`)}
      <Title>{identityPlayer.displayName}</Title>

      {paywallModal}

      <PlayerProfileHero
        displayName={identityPlayer.displayName}
        fullName={identityPlayer.fullName}
        position={identityPlayer.position}
        teamName={identityPlayer.team.fullName ?? identityPlayer.team.shortName}
        price={identityPlayer.price}
        status={identityPlayer.status ?? null}
        chanceOfPlaying={identityPlayer.chanceOfPlaying ?? null}
        news={identityPlayer.news}
        newsAddedAt={identityPlayer.newsAddedAt}
        imageUrl={identityPlayer.imageUrl}
        kitImageUrl={identityPlayer.kitImageUrl}
        seasonPoints={heroSeasonPoints}
        pointsPerGame={pointsPerGame}
        ownership={identityPlayer.ownership ?? null}
        minutesPerStart={minutesPerStart}
        comparisonHref={`${COMPARISON_TOOL_PATH}?a=${optaId}`}
        optaHref={OPTA_PATH}
        breadcrumbs={breadcrumbs}
        onPlayerSearchClick={handlePlayerClick}
        onPaywallOpen={openPaywall}
      />

      <PlayerProfileFilterBar
        filters={filters}
        seasons={selectableSeasons}
        onSeasonChange={(season) => setFilter('season', season)}
        onVenueChange={(venue) => setFilter('venue', venue)}
        onWindowChange={(window) => setFilter('window', window)}
        onBasisChange={(basis) => setFilter('basis', basis)}
        isRestricted={isRestricted}
        onRestrictedClick={openPaywall}
        stickyOffsetPx={isAppView ? 0 : FIXED_HEADER_HEIGHT_PX}
      />

      <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-5 sm:px-6 lg:px-12">
        {!hasPlayedInWindow && (
          <EmptyModule
            title={`No minutes for ${identityPlayer.displayName} in this window`}
            message={`${identityPlayer.displayName} has not featured in the ${filterSubtitle} selection. Widen the window or switch season to see recorded appearances.`}
          />
        )}

        {hasPlayedInWindow && (
          <>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.85fr_1fr]">
              <FormChart points={formChartPoints} subtitle={filterSubtitle} />
              {isRestricted ? (
                <LockedModulePanel
                  title="Over / underperformance"
                  subtitle="Returns against what the underlying numbers expected"
                  message="See whether returns are running ahead of or behind expected points, goals and assists."
                  onUnlockClick={openPaywall}
                />
              ) : (
                <PerformanceDeltas
                  decimals={decimals}
                  deltas={[
                    {
                      label: 'Points',
                      expectedLabel: 'xPts',
                      actual: readStat(statsRow, 'fpl.points'),
                      expected: readStat(statsRow, 'fpl.expectedPoints')
                    },
                    {
                      label: 'Goals',
                      expectedLabel: 'xG',
                      actual: readStat(statsRow, 'shooting.goals.total'),
                      expected: readStat(statsRow, 'shooting.expectedGoals')
                    },
                    {
                      label: 'Assists',
                      expectedLabel: 'xA',
                      actual: readStat(statsRow, 'creativity.assists'),
                      expected: readStat(statsRow, 'creativity.expectedAssists')
                    }
                  ]}
                />
              )}
            </div>

            {isRestricted ? (
              <LockedModulePanel
                title={underlying.title}
                subtitle={underlying.subtitle}
                message={`See every underlying number ranked against other ${position} players with enough minutes to compare.`}
                onUnlockClick={openPaywall}
              />
            ) : (
              <UnderlyingMetrics
                title={underlying.title}
                subtitle={`OPTA data · ${filterSubtitle} · percentile against ${populationSize} other ${position} players with enough minutes`}
                rows={underlyingRows}
                decimals={decimals}
                optaHref={OPTA_PATH}
              />
            )}

            {hasDefensiveContribution && defensiveRule && !isRestricted && (
              <DefensiveContribution
                metricLabel={defensiveRule.metricLabel}
                threshold={defensiveRule.threshold}
                ruleDescription={defensiveRule.ruleDescription}
                actionsOnBasis={readStat(statsRow, defensiveRule.statPath)}
                pointsBanked={defensiveContributionPointsBanked}
                gameweeks={defensiveGameweeks}
                hitRateBasis={hitRateBasis}
                onHitRateBasisChange={setHitRateBasis}
                basisLabel={
                  filters.basis === 'total'
                    ? 'total'
                    : filters.basis === 'per90'
                    ? 'per 90'
                    : 'per game'
                }
              />
            )}

            {hasDefensiveContribution && isRestricted && (
              <LockedModulePanel
                title="Defensive contribution"
                subtitle={defensiveRule?.ruleDescription}
                message="See how often this player clears the defensive contribution threshold, and how close they get when they miss."
                onUnlockClick={openPaywall}
              />
            )}

            {/* Keyed on the rule, not the position: the placeholder only
                makes sense for positions that would show the module in a
                season that has the data. */}
            {!hasDefensiveContribution && defensiveRule && (
              <EmptyModule
                title="Defensive contribution"
                message={`Defensive contribution points started in ${toSeasonLabel(
                  DEFENSIVE_CONTRIBUTION_FROM_SEASON
                )}, and the underlying action counts do not exist for earlier seasons. Switch season to see this module.`}
              />
            )}
          </>
        )}

        <UpcomingFixtures
          fixtures={upcomingFixtures}
          isRestricted={isRestricted}
          onUnlockClick={openPaywall}
        />

        <GameweekLog
          rows={gameweekLogRows}
          defensiveActionsLabel={
            hasDefensiveContribution && defensiveRule
              ? defensiveRule.metricLabel
              : ''
          }
          isRestricted={isRestricted}
          onUnlockClick={openPaywall}
          {...(previousSeason !== null
            ? {
                previousSeasonLabel: toSeasonLabel(previousSeason),
                onPreviousSeasonClick: () => setFilter('season', previousSeason)
              }
            : {})}
        />

        <SummaryBlock
          displayName={identityPlayer.displayName}
          position={identityPlayer.position}
          teamName={
            identityPlayer.team.fullName ?? identityPlayer.team.shortName
          }
          season={filters.season}
          price={identityPlayer.price}
          ownership={identityPlayer.ownership ?? null}
          pointsPerGame={pointsPerGame}
          expectedPointsPerGame={expectedPointsPerGame}
          comparisonLinks={comparisonLinks}
          clubmateLinks={clubmateLinks}
        />
      </div>
    </div>
  );
};
